import React, { createContext, useContext, useEffect, useState, useRef } from 'react';

interface LocationContextType {
    country: string | null;
    countryCode: string | null;
    loading: boolean;
    error: string | null;
}

interface CachedLocationData {
    country: string;
    countryCode: string;
    timestamp: number;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);
const CACHE_KEY = 'finants_geolocation_cache';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

const getCachedLocation = (): CachedLocationData | null => {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return null;
        
        const data = JSON.parse(cached) as CachedLocationData;
        const isExpired = Date.now() - data.timestamp > CACHE_DURATION;
        
        if (isExpired) {
            localStorage.removeItem(CACHE_KEY);
            return null;
        }
        
        return data;
    } catch {
        // If parsing fails, remove the corrupted cache
        localStorage.removeItem(CACHE_KEY);
        return null;
    }
};

const saveCachedLocation = (country: string, countryCode: string): void => {
    try {
        const data: CachedLocationData = {
            country,
            countryCode,
            timestamp: Date.now()
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch {
        // Silently fail if localStorage is unavailable
        console.warn('Failed to save location to cache');
    }
};

const isSignupPage = (): boolean => {
    return window.location.pathname === '/signup';
};

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [country, setCountry] = useState<string | null>(null);
    const [countryCode, setCountryCode] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hasCalledRef = useRef(false);

    useEffect(() => {
        if (hasCalledRef.current) return;
        
        // Only attempt geolocation on signup page
        if (!isSignupPage()) {
            setLoading(false);
            return;
        }
        
        hasCalledRef.current = true;
        const fetchLocation = async () => {
            try {
                setLoading(true);
                setError(null);

                // Check cache first
                const cached = getCachedLocation();
                if (cached) {
                    setCountry(cached.country);
                    setCountryCode(cached.countryCode);
                    setLoading(false);
                    return;
                }

                // Using freeipapi geolocation endpoint
                // Uses the current request's IP address (from browser)
                const response = await fetch('https://free.freeipapi.com/api/json/', {
                    method: 'GET',
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                // Map freeipapi response to our format
                setCountry(data.countryName);
                setCountryCode(data.countryCode);
                
                // Save to cache
                saveCachedLocation(data.countryName, data.countryCode);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to determine location';
                setError(errorMessage);
                console.warn('Location detection failed:', errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchLocation();
    }, []);

    const value: LocationContextType = {
        country,
        countryCode,
        loading,
        error,
    };

    return (
        <LocationContext.Provider value={value}>
            {children}
        </LocationContext.Provider>
    );
};

/**
 * Hook to access location context
 * @returns Location context including country, countryCode, loading, and error
 */
export const useGeolocation = () => {
    const context = useContext(LocationContext);
    if (context === undefined) {
        throw new Error('useGeolocation must be used within a LocationProvider');
    }
    return context;
};
